import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import NationalityDisplay from 'components/NationalityDisplay';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`patient-tabpanel-${index}`}
      aria-labelledby={`patient-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

function PatientOverview({ patientData, spouseData }) {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Reset tab to 0 if spouse data is not available and user is on spouse tab
  useEffect(() => {
    if (!spouseData && tabValue === 1) {
      setTabValue(0);
    }
  }, [spouseData, tabValue]);

  const getGenderDisplay = (gender) => {
    switch (gender) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'O':
        return 'Other';
      case 'U':
        return 'Unspecified';
      default:
        return 'N/A';
    }
  };

  const getGenderColor = (gender) => {
    switch (gender) {
      case 'M':
        return '#2196f3';
      case 'F':
        return '#e91e63';
      case 'O':
        return '#ff9800';
      case 'U':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const InfoCard = ({ label, value, color = '#00798E', bgColor = '#e0f7fa' }) => (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: bgColor,
        border: `1px solid ${color}30`,
        minHeight: '60px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography variant="caption" color={color} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        color="textPrimary"
        sx={{ fontWeight: 'medium', fontSize: '0.85rem' }}
      >
        {value || 'N/A'}
      </Typography>
    </Box>
  );

  InfoCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    color: PropTypes.string,
    bgColor: PropTypes.string,
  };

  const PersonInfo = ({ person, isSpouse = false }) => {
    if (!person || (!isSpouse && !person.id) || (isSpouse && !person.id)) {
      return (
        <Box textAlign="center" py={4}>
          <PersonIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {isSpouse ? 'No spouse information available' : 'No patient information available'}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {/* Header with Avatar and Basic Info */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar
              src={
                person.profile_picture_base_url && person.profile_picture_path
                  ? `${person.profile_picture_base_url}${person.profile_picture_path}`
                  : person.profile_picture || person.image || person.photo
              }
              sx={{
                bgcolor: getGenderColor(person.gender),
                width: 50,
                height: 50,
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" fontWeight="bold" color="textPrimary">
                {person.name}
              </Typography>
              <Box display="flex" gap={1} mt={0.5}>
                <Chip
                  label={getGenderDisplay(person.gender)}
                  size="small"
                  sx={{
                    bgcolor: getGenderColor(person.gender),
                    color: 'white',
                    fontSize: '0.7rem',
                  }}
                />
                {person.marital_status && (
                  <Chip
                    label={person.marital_status}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <InfoCard
            label="Phone Number"
            value={person.phone_number}
            color="#4caf50"
            bgColor="#e8f5e8"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard label="Email Address" value={person.email} color="#2196f3" bgColor="#e3f2fd" />
        </Grid>

        {/* Codes */}
        <Grid item xs={12} md={6}>
          <InfoCard
            label={isSpouse ? 'Spouse Code' : 'Patient Code'}
            value={person.external_code}
            color="#7b1fa2"
            bgColor="#f3e5f5"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard
            label="Couple Code"
            value={person.couple_code}
            color="#f57c00"
            bgColor="#fff3e0"
          />
        </Grid>

        {/* Professional Information */}
        {person.occupation && (
          <Grid item xs={12} md={6}>
            <InfoCard
              label="Occupation"
              value={person.occupation}
              color="#2e7d32"
              bgColor="#e8f5e8"
            />
          </Grid>
        )}

        {/* Nationality */}
        <Grid item xs={12} md={person.occupation ? 6 : 12}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0',
              minHeight: '60px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              color="#666"
              sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
            >
              Nationality
            </Typography>
            <NationalityDisplay
              nationality={person.nationality}
              nationalityId={person.nationality_id}
              variant="body2"
              color="textPrimary"
              showLabel={false}
              inline={true}
              flagSize="16"
            />
          </Box>
        </Grid>

        {/* Marriage Information */}
        {person.marital_status === 'married' && person.married_for && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Chip label="Marriage Information" size="small" />
              </Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoCard
                label="Married For"
                value={person.married_for}
                color="#1976d2"
                bgColor="#e3f2fd"
              />
            </Grid>
            {person.trying_for && (
              <Grid item xs={12} md={6}>
                <InfoCard
                  label="Trying For"
                  value={person.trying_for}
                  color="#9c27b0"
                  bgColor="#f3e5f5"
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    );
  };

  PersonInfo.propTypes = {
    person: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      external_code: PropTypes.string,
      couple_code: PropTypes.string,
      gender: PropTypes.string,
      phone_number: PropTypes.string,
      email: PropTypes.string,
      occupation: PropTypes.string,
      nationality_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nationality: PropTypes.string,
      marital_status: PropTypes.string,
      married_for: PropTypes.string,
      trying_for: PropTypes.string,
      profile_picture_base_url: PropTypes.string,
      profile_picture_path: PropTypes.string,
      profile_picture: PropTypes.string,
      image: PropTypes.string,
      photo: PropTypes.string,
    }),
    isSpouse: PropTypes.bool,
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: '64px',
              fontSize: '0.95rem',
              fontWeight: 'medium',
            },
            '& .Mui-selected': {
              color: '#00798E !important',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#00798E',
              height: 3,
            },
          }}
        >
          <Tab
            icon={<PersonIcon />}
            iconPosition="start"
            label="Patient Information"
            sx={{ gap: 1 }}
          />
          {spouseData && (
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label="Spouse Information"
              sx={{ gap: 1 }}
            />
          )}
        </Tabs>
      </Box>

      <CardContent sx={{ p: 0 }}>
        <TabPanel value={tabValue} index={0}>
          <PersonInfo person={patientData} isSpouse={false} />
        </TabPanel>

        {spouseData && (
          <TabPanel value={tabValue} index={1}>
            <PersonInfo person={spouseData} isSpouse={true} />
          </TabPanel>
        )}
      </CardContent>

      {/* Quick Switch Button */}
      {spouseData && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        >
          <IconButton
            onClick={() => setTabValue(tabValue === 0 ? 1 : 0)}
            sx={{
              bgcolor: 'rgba(0, 121, 142, 0.1)',
              color: '#00798E',
              '&:hover': {
                bgcolor: 'rgba(0, 121, 142, 0.2)',
              },
            }}
            size="small"
          >
            <SwapHorizIcon />
          </IconButton>
        </Box>
      )}
    </Card>
  );
}

PatientOverview.propTypes = {
  patientData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    external_code: PropTypes.string,
    couple_code: PropTypes.string,
    gender: PropTypes.string,
    phone_number: PropTypes.string,
    email: PropTypes.string,
    occupation: PropTypes.string,
    nationality_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nationality: PropTypes.string,
    marital_status: PropTypes.string,
    married_for: PropTypes.string,
    trying_for: PropTypes.string,
    profile_picture_base_url: PropTypes.string,
    profile_picture_path: PropTypes.string,
    profile_picture: PropTypes.string,
    image: PropTypes.string,
    photo: PropTypes.string,
  }),
  spouseData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    external_code: PropTypes.string,
    couple_code: PropTypes.string,
    gender: PropTypes.string,
    phone_number: PropTypes.string,
    email: PropTypes.string,
    occupation: PropTypes.string,
    nationality_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nationality: PropTypes.string,
    marital_status: PropTypes.string,
    married_for: PropTypes.string,
    trying_for: PropTypes.string,
    profile_picture_base_url: PropTypes.string,
    profile_picture_path: PropTypes.string,
    profile_picture: PropTypes.string,
    image: PropTypes.string,
    photo: PropTypes.string,
  }),
};

export default PatientOverview;
